"use client";

import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { useCallback, useEffect, useRef, useState } from "react";

// A removal is only sent after the undo window closes, so "Cofnij" is just a
// cleared timeout — nothing to restore on the cart side (MPL-3899).
export const REMOVAL_UNDO_MS = 4000;

export type PendingRemoval =
  | { kind: "diet"; itemId: string }
  | {
      kind: "addon";
      itemId: string;
      addonId: string;
      // Needed to report the removal to GTM at commit time; the catalog is
      // already loaded where the removal is triggered.
      categories: DietAddonGroupDietAddonsRead[];
      cateringName?: string;
    };

type ScheduledRemoval = {
  removal: PendingRemoval;
  timeout: ReturnType<typeof setTimeout>;
};

// "undo" — waiting out its window, still revocable. "committing" — the write is
// on its way and the position cannot come back, but the cart still holds it
// until the response lands, so the card has to stay hidden.
export type RemovalStatus = "idle" | "undo" | "committing";

const removalKey = (itemId: string, addonId?: string) =>
  addonId ? `addon:${itemId}:${addonId}` : `diet:${itemId}`;

export const useRemovalQueue = (
  commit: (removal: PendingRemoval) => void | Promise<unknown>,
) => {
  const scheduledRef = useRef(new Map<string, ScheduledRemoval>());
  const [keys, setKeys] = useState<string[]>([]);
  const [committingKeys, setCommittingKeys] = useState<string[]>([]);

  // Read through a ref so a timeout that fires (or a flush) always commits with
  // the current cart state instead of the one captured when it was queued.
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  }, [commit]);

  // Holding the key until the write settles keeps the position hidden for the
  // whole round trip; dropping it when the request leaves would flash the card
  // back for as long as the cart takes to answer.
  const runCommit = useCallback((key: string, removal: PendingRemoval) => {
    setCommittingKeys((current) => [...current, key]);

    return Promise.resolve(commitRef.current(removal))
      .catch(() => undefined)
      .finally(() =>
        setCommittingKeys((current) =>
          current.filter((entry) => entry !== key),
        ),
      );
  }, []);

  const queue = useCallback(
    (removal: PendingRemoval) => {
      const scheduled = scheduledRef.current;
      const key = removalKey(
        removal.itemId,
        removal.kind === "addon" ? removal.addonId : undefined,
      );

      // Dropping the whole diet cancels the pending removals of its addons: the
      // addon write appends the item, so it would re-add a diet already gone.
      if (removal.kind === "diet") {
        for (const [scheduledKey, entry] of scheduled) {
          if (scheduledKey.startsWith(`addon:${removal.itemId}:`)) {
            clearTimeout(entry.timeout);
            scheduled.delete(scheduledKey);
          }
        }
      }

      clearTimeout(scheduled.get(key)?.timeout);

      const timeout = setTimeout(() => {
        scheduled.delete(key);
        setKeys([...scheduled.keys()]);
        runCommit(key, removal);
      }, REMOVAL_UNDO_MS);

      scheduled.set(key, { removal, timeout });
      setKeys([...scheduled.keys()]);
    },
    [runCommit],
  );

  const cancel = useCallback((itemId: string, addonId?: string) => {
    const scheduled = scheduledRef.current;
    const key = removalKey(itemId, addonId);
    const entry = scheduled.get(key);
    if (!entry) {
      return;
    }

    clearTimeout(entry.timeout);
    scheduled.delete(key);
    setKeys([...scheduled.keys()]);
  }, []);

  const getRemovalStatus = useCallback(
    (itemId: string, addonId?: string): RemovalStatus => {
      const key = removalKey(itemId, addonId);

      if (keys.includes(key)) {
        return "undo";
      }

      return committingKeys.includes(key) ? "committing" : "idle";
    },
    [committingKeys, keys],
  );

  // Leaving the cart counts as confirming the removal, so everything still
  // waiting out its undo window is sent instead of dropped (MPL-3899). The
  // promise lets the caller wait out the writes before it navigates away.
  const flush = useCallback(() => {
    const scheduled = scheduledRef.current;
    const commits = [...scheduled].map(([key, { removal, timeout }]) => {
      clearTimeout(timeout);
      return runCommit(key, removal);
    });

    scheduled.clear();
    setKeys([]);

    return Promise.all(commits);
  }, [runCommit]);

  useEffect(() => {
    // pagehide covers what the unmount cleanup cannot: a closed tab or a hard
    // navigation. The cart writes are sent with `keepalive` for that reason.
    window.addEventListener("pagehide", flush);

    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [flush]);

  return { cancel, flush, getRemovalStatus, queue };
};
